import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { CATEGORIES } from '../Categories/constant';
import {
  FooterContainer,
  Container,
  TopSection,
  BrandColumn,
  Logo,
  LogoImage,
  BrandDescription,
  SocialLinks,
  SocialLink,
  Column,
  ColumnTitle,
  LinkList,
  LinkItem,
  FooterLink,
  BottomSection,
  Copyright,
  LegalLinks,
  LegalLink,
  InfoText,
  ContactLink,
  MapContainer,
} from './Footer.styled';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleLogoClick = () => {
    navigate('/');
  };

  const socialLinks = [
    {
      name: 'Facebook',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
      url: 'https://www.facebook.com/share/1BnCfHe56h/?mibextid=wwXIfr',
    },
    {
      name: 'Instagram',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      ),
      url: 'https://www.instagram.com/swanpipesupport?igsh=d3I2cmtiajgyeGs3',
    },
    {
      name: 'YouTube',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.016 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
      url: 'https://youtube.com/@swanpipesupportpvtltd?si=2XX6eUX_RflEI8zs',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <FooterContainer
      $isHomePage={isHomePage}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8 }}
    >
      <Container>
        <TopSection
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <BrandColumn variants={itemVariants}>
            <Logo
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 400 }}
              onClick={handleLogoClick}
            >
              <LogoImage
                src="/img/swan-logo.png"
                alt="SWAN Logo"
                $isHomePage={isHomePage}
              />
            </Logo>
            <BrandDescription $isHomePage={isHomePage}>
              High-quality support systems engineered for Plumbing, Electrical,
              HVAC, and Fire-fighting applications.
            </BrandDescription>
            <SocialLinks>
              {socialLinks.map((social, index) => (
                <SocialLink
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                  $isHomePage={isHomePage}
                >
                  {social.icon}
                </SocialLink>
              ))}
            </SocialLinks>
          </BrandColumn>

          <Column variants={itemVariants}>
            <ColumnTitle $isHomePage={isHomePage}>Navigation</ColumnTitle>
            <LinkList>
              <LinkItem>
                <FooterLink to="/" $isHomePage={isHomePage}>
                  Home
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="/products" $isHomePage={isHomePage}>
                  Products
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="/about" $isHomePage={isHomePage}>
                  About
                </FooterLink>
              </LinkItem>
              <LinkItem>
                <FooterLink to="/contact" $isHomePage={isHomePage}>
                  Contact
                </FooterLink>
              </LinkItem>
            </LinkList>
          </Column>

          <Column variants={itemVariants}>
            <ColumnTitle $isHomePage={isHomePage}>Products</ColumnTitle>
            <LinkList>
              {CATEGORIES.map((category) => (
                <LinkItem key={category.slug}>
                  <FooterLink
                    to={`/products/${category.slug}`}
                    $isHomePage={isHomePage}
                  >
                    {category.name
                      .toLowerCase()
                      .replace(/\b\w/g, (char) => char.toUpperCase())}
                  </FooterLink>
                </LinkItem>
              ))}
            </LinkList>
          </Column>

          <Column variants={itemVariants}>
            <ColumnTitle $isHomePage={isHomePage}>Contact Us</ColumnTitle>
            <InfoText $isHomePage={isHomePage}>
              <ContactLink
                as="div"
                $isHomePage={isHomePage}
                style={{ cursor: 'default' }}
              >
                <span>Address</span>
                <strong>
                  Swan Pipe Support Pvt. Ltd.
                  <br />
                  No. 20, Chockalinga Nagar 5th Street,
                  <br />
                  Koodal Nagar, Madurai,
                  <br />
                  Tamil Nadu 625018
                </strong>
              </ContactLink>
              <ContactLink as="div" $isHomePage={isHomePage}>
                <span>Call Us</span>
                <strong>
                  <a href="tel:+919600342059">+91 96003 42059</a>
                  <a href="tel:+917305940534">+91 73059 40534</a>
                  <a href="tel:+917305940514">+91 73059 40514</a>
                </strong>
              </ContactLink>
              <ContactLink as="div" $isHomePage={isHomePage}>
                <span>Mail Us</span>
                <strong>
                  <a href="mailto:info@swanclamps.com">info@swanclamps.com</a>
                </strong>
              </ContactLink>
            </InfoText>
          </Column>

          <Column variants={itemVariants}>
            <ColumnTitle $isHomePage={isHomePage}>Location</ColumnTitle>
            <MapContainer $isHomePage={isHomePage}>
              <iframe
                title="Swan Pipe Support Pvt Ltd"
                src="https://maps.google.com/maps?q=Swan%20Pipe%20Support%20Pvt%20Ltd%20JLS%20building%20Madurai&t=&z=15&ie=UTF8&iwloc=&output=embed"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </MapContainer>
          </Column>
        </TopSection>

        <BottomSection $isHomePage={isHomePage}>
          <Copyright
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            $isHomePage={isHomePage}
          >
            © {new Date().getFullYear()} Swan. All rights reserved.
          </Copyright>
          <LegalLinks>
            <LegalLink
              to="/privacy"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              $isHomePage={isHomePage}
            >
              Privacy Policy
            </LegalLink>
            <LegalLink
              to="/terms"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              $isHomePage={isHomePage}
            >
              Terms & Conditions
            </LegalLink>
          </LegalLinks>
        </BottomSection>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
